import React, { useRef, useState } from 'react';
import {
  DateRangeControl,
  InfoTip,
  MetricKey,
  STICKY_FIRST_COL,
  defaultRange,
  rangePreset,
} from '@/components/shared';
import { ErrorBanner, Spinner } from '../shared';
import {
  Audience,
  FailureReason,
  OpenStatsRow,
  RecipientScope,
  ScopeInfo,
  StatsBucket,
  StatsWindow,
  useNotificationOpenStats,
} from './api/useNotificationOpenStats';

/**
 * Whether a count means "how many notifications" or "how many people".
 *
 * They diverge hard on broadcasts: one announcement to 900 users is 900
 * notifications and 900 people, but a chatty type that fires six times a week
 * at the same 70 people looks enormous in `total` and tiny in `users`. Open
 * rate flips meaning too — per delivered notification vs per person reached.
 */
type Mode = 'total' | 'users';

const MODES: { key: Mode; label: string }[] = [
  { key: 'total', label: 'Total' },
  { key: 'users', label: 'Distinct users' },
];

/**
 * Which recipients the numbers cover.
 *
 * Narrowing to reachable users answers a fair question — "of the people we can
 * actually deliver to, how are we doing" — and it is the right denominator for
 * anything venue-facing. It also removes nearly every `no_devices` failure,
 * which is the loudest evidence that the reachable base is tiny. So the default
 * stays on every send, and a narrowed view carries a banner saying what it
 * dropped.
 */
const SCOPES: { key: RecipientScope; label: string; hint: string }[] = [
  { key: 'all', label: 'Everyone', hint: 'every send attempted' },
  { key: 'reachable', label: 'Has a device', hint: 'users with a registered device' },
  { key: 'active', label: 'Active', hint: 'has a device and opened the app in 30 days' },
];

/** States plainly what a narrowed view is not showing. */
function ScopeNotice({ scope }: { scope: ScopeInfo }) {
  if (scope.downgraded) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
        <p className="text-xs text-amber-300">
          Showing every send. The device table could not be read, so
          &ldquo;{scope.requested}&rdquo; could not be applied — the numbers below are
          unfiltered rather than filtered on a guess.
        </p>
      </div>
    );
  }
  if (scope.applied === 'all') return null;
  return (
    <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 px-4 py-3">
      <p className="text-xs text-violet-200">
        Filtered to {scope.users?.toLocaleString() ?? '—'} reachable
        {scope.applied === 'active' ? ', recently active' : ''} users. Hiding{' '}
        <span className="font-semibold">{scope.hidden_rows.toLocaleString()}</span> sends to{' '}
        <span className="font-semibold">{scope.hidden_users.toLocaleString()}</span> users who
        could not be reached — mostly people with no registered device. Rates below are per
        reachable user, not per user.
      </p>
    </div>
  );
}

type CountKey =
  | 'delivered'
  | 'delivered_tracked'
  | 'opened'
  | 'fulfilled'
  | 'engaged'
  | 'failed'
  | 'throttled'
  | 'attempted';
type RateKey = 'open_rate' | 'engaged_rate' | 'delivery_rate';

const count = (r: OpenStatsRow, k: CountKey, mode: Mode): number =>
  mode === 'users' ? (r[`${k}_users` as keyof OpenStatsRow] as number) : r[k];

const rate = (r: OpenStatsRow, k: RateKey, mode: Mode): number | null =>
  mode === 'users' ? (r[`${k}_users` as keyof OpenStatsRow] as number | null) : r[k];

const TYPE_LABELS: Record<string, string> = {
  deal: 'Deals',
  announcement: 'Announcements',
  gamification: 'Spark Coins',
  checkin_reminder: 'Check-in reminders',
  moment_reminder: 'Spark Moments',
  friend_request: 'Friend requests',
  friend_accepted: 'Friend accepted',
  friend_activity: 'Friend activity',
  livecheckin_confirmation: 'Live check-in',
  system_alert: 'System alerts',
  poke: 'Pokes',
};

const label = (t: string) => TYPE_LABELS[t] ?? t;

/** `—` for an unknown rate. A type with nothing delivered has no rate; showing 0% would read as a failure that never happened. */
const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

/** `—` for a count we could not compute, which is not the same as zero. */
const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString());

function Tile({
  label: text,
  value,
  sub,
  tone = 'default',
  info,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'warn' | 'good';
  info?: MetricKey;
}) {
  const toneClass =
    tone === 'warn' ? 'text-amber-400' : tone === 'good' ? 'text-emerald-400' : 'text-white';
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
        {text}
        {info && <InfoTip metric={info} />}
      </p>
      <p className={`text-2xl font-bold tabular-nums mt-1 ${toneClass}`}>{value}</p>
      {sub && <p className="text-xs text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * Who we can reach at all.
 *
 * Two different kinds of dead, kept apart on purpose. "Dormant" is a retention
 * problem: the account is alive but nobody has opened the app in months.
 * "No device" is a plumbing problem: we could not deliver even if they were
 * here. They overlap heavily but are not the same set, and the fix for one does
 * nothing for the other.
 */
function AudiencePanel({ a }: { a: Audience }) {
  const unknownDevices = a.reachable_users === null;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Audience</p>
        <p className="text-xs text-gray-600">
          Live accounts only. &quot;Opted in&quot; means the user has at least one registered
          device — iOS never reports the permission itself, so a denied prompt and a prompt never
          shown look identical here.
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile label="Total users" value={num(a.total_users)} sub="active accounts" />
        <Tile
          label="Opted in"
          value={num(a.reachable_users)}
          sub={unknownDevices ? 'device table unavailable' : `${pct(a.reachable_pct)} of all users`}
          tone={unknownDevices ? 'default' : 'good'}
        />
        <Tile
          label="No device"
          value={num(a.unreachable_users)}
          sub={unknownDevices ? 'device table unavailable' : 'cannot be reached by push'}
          tone={unknownDevices ? 'default' : 'warn'}
        />
        <Tile
          label={`Dormant ${a.dormant_days}d+`}
          value={num(a.dormant_users)}
          sub={`${pct(a.dormant_pct)} · ${num(a.never_seen_users)} never seen`}
          tone="warn"
        />
        <Tile
          label="Addressable"
          info="notif_addressable"
          value={num(a.active_reachable_users)}
          sub="seen recently AND reachable"
          tone="good"
        />
      </div>
      {a.orphan_device_users !== null && a.orphan_device_users > 0 && (
        <p className="text-xs text-gray-600 mt-2">
          {num(a.device_rows_users)} users hold a device token, but {num(a.orphan_device_users)} of
          them no longer exist as live accounts — deleted users whose token row has not yet expired.
          They are excluded from &quot;Opted in&quot;.
        </p>
      )}
    </div>
  );
}

function StatsTable({ rows, mode }: { rows: OpenStatsRow[]; mode: Mode }) {
  const td = 'px-3 py-2 text-sm text-gray-300';
  const tdNum = `${td} text-right tabular-nums`;
  const th = 'px-3 py-2 text-right text-xs text-gray-500 uppercase tracking-wider';
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-x-auto">
      <table className={`w-full min-w-[640px] ${STICKY_FIRST_COL} [&_td:first-child]:bg-gray-900`}>
        <thead>
          <tr className="border-b border-gray-800">
            <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase tracking-wider">Type</th>
            <th className={th}>Delivered</th>
            <th className={th}>Opened</th>
            <th className={th}>
              Fulfilled
              <InfoTip metric="notif_fulfilled" />
            </th>
            <th className={th}>
              Open rate
              <InfoTip metric="notif_open_rate" />
            </th>
            <th className={th}>Failed</th>
            <th className={th}>
              Delivery
              <InfoTip metric="notif_delivery_rate" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className={td} colSpan={7}>
                Nothing sent in this window.
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const failed = count(r, 'failed', mode);
            return (
              <tr key={r.notification_type} className="border-b border-gray-800/50 last:border-0">
                <td className={td}>{label(r.notification_type)}</td>
                <td className={tdNum}>{count(r, 'delivered', mode).toLocaleString()}</td>
                <td className={tdNum}>{count(r, 'opened', mode).toLocaleString()}</td>
                <td className={tdNum}>{count(r, 'fulfilled', mode).toLocaleString()}</td>
                <td className={tdNum}>{pct(rate(r, 'open_rate', mode))}</td>
                <td className={`${tdNum} ${failed > count(r, 'delivered', mode) ? 'text-amber-400' : ''}`}>
                  {failed.toLocaleString()}
                </td>
                <td className={tdNum}>{pct(rate(r, 'delivery_rate', mode))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Why sends did not land. Split by whether it represents a defect, because the
 * raw count conflates two unrelated things: a user with no push token is a
 * reachability gap worth fixing, while quiet hours and the daily caps are the
 * platform correctly declining to send. Reading them as one number makes
 * healthy suppression look like breakage.
 */
function FailuresPanel({
  rows,
  scope,
  mode,
}: {
  rows: FailureReason[];
  scope: string;
  mode: Mode;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 py-3">Nothing failed or was held back in this window.</p>;
  }
  const value = (r: FailureReason) => (mode === 'users' ? r.users : r.count);
  const gaps = rows.filter((r) => r.fault !== 'intended').reduce((a, r) => a + value(r), 0);
  const intended = rows.filter((r) => r.fault === 'intended').reduce((a, r) => a + value(r), 0);
  const unit = mode === 'users' ? 'users' : 'sends';

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        {scope}: <span className="text-amber-400 font-medium">{gaps.toLocaleString()}</span> never
        reachable ·{' '}
        <span className="text-gray-300 font-medium">{intended.toLocaleString()}</span> deliberately
        held back ({unit}). Only the first group is a problem to fix.
        {mode === 'users' && ' User counts across reasons can overlap — one person can hit several.'}
      </p>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl divide-y divide-gray-800/60">
        {rows.map((r) => (
          <div key={r.reason} className="flex items-start gap-3 px-4 py-3">
            <span
              className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                r.fault === 'intended' ? 'bg-gray-600' : 'bg-amber-500'
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-200">
                {r.label}
                <span className="ml-2 text-xs text-gray-600 font-mono">{r.reason}</span>
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{r.detail}</p>
            </div>
            <p className="text-sm font-semibold text-white tabular-nums">
              {value(r).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Notification open rates — the audience we can reach, the deal funnel on its
 * own, then every type.
 *
 * Rates are per DELIVERED notification, never per attempted send: most
 * attempts on prod never reach a device (no registered token), and dividing by
 * attempts understates open rate by more than an order of magnitude. The failed
 * column keeps that delivery problem visible instead of burying it in the
 * denominator.
 */
export function NotificationOpensTab() {
  const [[from, to], setRange] = useState<[string | null, string | null]>(defaultRange);
  const [mode, setMode] = useState<Mode>('total');
  // Defaults to every send: the unfiltered view is the honest one.
  const [scope, setScope] = useState<RecipientScope>('all');

  // A custom range only takes effect once BOTH ends are set — otherwise every
  // keystroke in the first input would fire a query for a half-specified window.
  // Until then the last complete window stays on screen.
  const preset = rangePreset(from, to);
  const lastWin = useRef<StatsWindow>({ days: 30 });
  if (preset !== null) lastWin.current = { days: preset };
  else if (from && to) lastWin.current = { from, to };
  const win = lastWin.current;

  const { data, loading, error } = useNotificationOpenStats(win, scope);
  const deals = data?.deals ?? null;
  const unit = mode === 'users' ? 'users' : 'notifications';

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-white">Notification open rates</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Rates are per notification actually delivered to a device, not per send attempted.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <DateRangeControl from={from} to={to} onChange={(f, t) => setRange([f, t])} />

          {/* Recipient scope. Unlike the count mode, this one refetches. */}
          <div className="flex items-center rounded-md border border-gray-800 bg-gray-900 ml-2 p-0.5">
            {SCOPES.map((sc) => (
              <button
                key={sc.key}
                onClick={() => setScope(sc.key)}
                aria-pressed={scope === sc.key}
                title={sc.hint}
                className={`px-2.5 py-1 text-xs font-medium rounded ${
                  scope === sc.key
                    ? 'bg-violet-950/60 text-violet-300'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {sc.label}
              </button>
            ))}
          </div>

          {/* Count mode. Both families ship in one response, so this never refetches. */}
          <div className="flex items-center rounded-md border border-gray-800 bg-gray-900 ml-2 p-0.5">
            {MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                aria-pressed={mode === m.key}
                className={`px-2.5 py-1 text-xs font-medium rounded ${
                  mode === m.key
                    ? 'bg-violet-950/60 text-violet-300'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {!loading && data && (
        <>
          <ScopeNotice scope={data.scope} />
          <AudiencePanel a={data.audience} />

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">
              Deal notifications · counting {unit}
            </p>
            {deals === null ? (
              <p className="text-sm text-gray-500 py-4">No deal notifications sent in this window.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Tile
                  label="Delivered"
                  value={count(deals, 'delivered', mode).toLocaleString()}
                  sub={`of ${count(deals, 'attempted', mode).toLocaleString()} attempted`}
                />
                <Tile
                  label="Opened"
                  value={count(deals, 'opened', mode).toLocaleString()}
                  sub={mode === 'users' ? 'people who tapped' : 'tapped the push'}
                />
                <Tile
                  label="Fulfilled"
                  info="notif_fulfilled"
                  value={count(deals, 'fulfilled', mode).toLocaleString()}
                  sub="acted on in-app"
                />
                <Tile
                  label="Open rate"
                  info="notif_open_rate"
                  value={pct(rate(deals, 'open_rate', mode))}
                  sub="of delivered"
                />
                <Tile
                  label="Failed"
                  value={count(deals, 'failed', mode).toLocaleString()}
                  sub="never reached a device"
                  tone={
                    count(deals, 'failed', mode) > count(deals, 'delivered', mode)
                      ? 'warn'
                      : 'default'
                  }
                />
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">
              Why deal notifications didn&apos;t land
            </p>
            <FailuresPanel rows={data.deal_failure_reasons} scope="Deals" mode={mode} />
          </div>

          {data.buckets
            .filter((b) => b.rows.length > 0)
            .map((b: StatsBucket) => (
              <div key={b.bucket}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                    {b.label}
                  </p>
                  <p className="text-xs text-gray-600">
                    {count(b.totals, 'delivered', mode).toLocaleString()} delivered ·{' '}
                    {count(b.totals, 'opened', mode).toLocaleString()} opened ·{' '}
                    {pct(rate(b.totals, 'open_rate', mode))}
                    {count(b.totals, 'delivered_tracked', mode) === 0 &&
                      count(b.totals, 'delivered', mode) > 0 && (
                        <span className="text-amber-500/80"> · opens not instrumented in this window</span>
                      )}
                  </p>
                </div>
                <StatsTable rows={b.rows} mode={mode} />
              </div>
            ))}

          <p className="text-xs text-gray-600">
            Across every type: {count(data.totals, 'delivered', mode).toLocaleString()} delivered,{' '}
            {count(data.totals, 'opened', mode).toLocaleString()} opened (
            {pct(rate(data.totals, 'open_rate', mode))}),{' '}
            {count(data.totals, 'failed', mode).toLocaleString()} failed — counting {unit}. Window{' '}
            {data.from.slice(0, 10)} → {data.to}.{' '}
            {mode === 'users' &&
              'Totals are distinct users computed across the whole window, so they are smaller than the sum of the rows above — one person receiving two types is counted once here. '}
            Delivery counts cover the whole window; open rates are measured only from{' '}
            {new Date(data.tracking_since).toLocaleDateString()}, when open tracking began — earlier
            sends carry no open signal, so they are left out of the rate rather than counted as
            unopened.
          </p>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">
              Why sends didn&apos;t land — all types
            </p>
            <FailuresPanel rows={data.failure_reasons} scope="All types" mode={mode} />
          </div>
        </>
      )}
    </div>
  );
}
